import React from 'react';
import { Page, Text, View, Document, StyleSheet, PDFViewer, PDFDownloadLink, Font, Image } from '@react-pdf/renderer';
import { BrowserView, MobileView } from 'react-device-detect';
import PdfPreview from 'ui-component/PdfPreview';
import { Stack, Typography } from '@mui/material';
import logo from 'assets/images/Logo-Aone-Final-Full.png';
import PaidStamp1000 from 'assets/images/PaidStamp1000.png';
import moment from 'moment';
import { ToWords } from 'to-words';

Font.register({ family: 'Noto Sans Lao', src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@master/hinted/ttf/NotoSansLao/NotoSansLao-Regular.ttf' });

const toWords = new ToWords({
    localeCode: 'en-US',
    converterOptions: { currency: false, ignoreDecimal: false, ignoreZeroCurrency: false, doNotAddOnly: false }
});

const formatCurrency = (num) => {
    if (num === undefined || num === null) return '0';
    return Number(num).toLocaleString('en-US');
};

const styles = StyleSheet.create({
    body: {
        paddingTop: 30,
        paddingBottom: 60,
        paddingHorizontal: 35,
        fontFamily: 'Noto Sans Lao'
    },
    title: {
        padding: 5,
        fontSize: 20,
        textAlign: 'center',
        fontWeight: 'bold'
    },
    section: {
        flexDirection: 'row',
        alignItems: 'center',
        fontSize: 9,
        marginBottom: 2
    },
    label: {
        width: 75,
        fontWeight: 'bold'
    },
    labelRight: {
        width: 75,
        fontWeight: 'bold'
    },
    value: {
        flex: 1
    },
    logo: {
        position: 'absolute',
        top: 20,
        left: 35,
        width: 80
    },
    stamps: {
        position: 'absolute',
        width: 300,
        opacity: 0.3,
        top: '40%',
        left: '25%'
    },
    table: {
        width: '100%',
        marginTop: 10
    },
    rowTable: {
        flexDirection: 'row',
        fontSize: 9
    },
    headerTable: {
        backgroundColor: '#f8f9fa',
        fontWeight: 'bold',
        borderTopWidth: 1,
        borderTopColor: '#000',
        borderTopStyle: 'solid'
    },
    cell: {
        borderLeftWidth: 1,
        borderLeftColor: '#000',
        borderLeftStyle: 'solid',
        borderBottomWidth: 1,
        borderBottomColor: '#000',
        borderBottomStyle: 'solid',
        padding: 4
    },
    lastCell: {
        borderRightWidth: 1,
        borderRightColor: '#000',
        borderRightStyle: 'solid'
    },
    // Column Widths for Credit Invoice
    colCustId: { width: '15%' },
    colCustName: { width: '30%' },
    colInvoiceNo: { width: '25%' },
    colTotal: { width: '15%' },
    colCredit: { width: '15%' },

    footerContainer: {
        marginTop: 10,
        borderTopWidth: 1,
        borderColor: '#000'
    },
    footerRow: {
        flexDirection: 'row',
        alignItems: 'stretch'
    },
    wordsBox: {
        width: '60%',
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 6,
        fontSize: 8,
        textAlign: 'center',
        justifyContent: 'center'
    },
    sideLabel: {
        width: '15%',
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 6,
        fontSize: 9,
        fontWeight: 'bold',
        justifyContent: 'center'
    },
    sideValue: {
        width: '25%',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 6,
        fontSize: 9,
        textAlign: 'right',
        justifyContent: 'center'
    },
    remarkLabel: {
        width: '15%',
        borderLeftWidth: 1,
        borderBottomWidth: 0,
        borderColor: '#000',
        padding: 6,
        fontWeight: 'bold',
        fontSize: 9,
        justifyContent: 'center'
    },
    remarkContent: {
        width: '45%',
        borderLeftWidth: 1,
        borderBottomWidth: 0,
        borderColor: '#000',
        padding: 6,
        fontSize: 8,
        justifyContent: 'center'
    },
    remarkLabelEnd: {
        width: '15%',
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 6,
        fontWeight: 'bold',
        fontSize: 9,
        justifyContent: 'center'
    },
    remarkContentEnd: {
        width: '45%',
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 6,
        fontSize: 8,
        justifyContent: 'center'
    },
    grandTotalLabel: {
        width: '60%',
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 8,
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: 10,
        backgroundColor: '#f8f9fa'
    },
    grandTotalValue: {
        width: '40%',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 8,
        textAlign: 'right',
        fontWeight: 'bold',
        fontSize: 10,
        backgroundColor: '#f8f9fa'
    }
});

const MyDocument = ({ data }) => (
    <Document>
        <Page size="A4" style={styles.body}>
            {data.status === 'paid' && <View style={styles.stamps}><Image src={PaidStamp1000} /></View>}
            <Image style={styles.logo} src={logo} />
            <Text style={styles.title}>Credit Invoice</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 15, marginBottom: 5 }}>
                <View style={{ width: '55%' }}>
                    <View style={styles.section}><Text style={styles.label}>Invoice no.</Text><Text style={styles.value}>: {data?.invoiceNumber}</Text></View>
                    <View style={styles.section}><Text style={styles.label}>Customer no.</Text><Text style={styles.value}>: {data?.customerID}</Text></View>
                    <View style={styles.section}><Text style={styles.label}>Mobile</Text><Text style={styles.value}>: {data?.customers?.mobile || 'N/A'}</Text></View>
                    <View style={styles.section}><Text style={styles.label}>Customer</Text><Text style={styles.value}>: {data?.customers?.customerName || 'N/A'}</Text></View>
                    <View style={styles.section}><Text style={styles.label}>Address</Text><Text style={styles.value}>: {`${data?.customers?.villages?.vill_name || ''}, ${data?.customers?.districts?.dr_name || ''}, ${data?.customers?.provinces?.pr_name || ''}`}</Text></View>
                </View>
                <View style={{ width: '40%' }}>
                    <View style={styles.section}><Text style={styles.labelRight}>Date</Text><Text style={styles.value}>: {moment(data?.invoiceDate).format('DD-MM-yyyy HH:mm')}</Text></View>
                    <View style={styles.section}><Text style={styles.labelRight}>CreateBy</Text><Text style={styles.value}>: {data?.CreateBy}</Text></View>
                    <View style={styles.section}><Text style={styles.labelRight}>BranchCode</Text><Text style={styles.value}>: {data?.branchCode || 'N/A'}</Text></View>
                </View>
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 5, marginBottom: 5 }}>
                <View style={{ width: '100%' }}>
                    <View style={styles.section}><Text style={styles.labelRight}>Description</Text><Text style={styles.value}>: {data?.description ? data?.description : 'N/A'}</Text></View>
                </View>
            </View>
            <View style={styles.table}>
                <View style={[styles.rowTable, styles.headerTable]} fixed>
                    <Text style={[styles.cell, styles.colCustId]}>Customer ID</Text>
                    <Text style={[styles.cell, styles.colCustName]}>Customer Name</Text>
                    <Text style={[styles.cell, styles.colInvoiceNo]}>Invoice Number</Text>
                    <Text style={[styles.cell, styles.colTotal]}>Total(₭)</Text>
                    <Text style={[styles.cell, styles.colCredit, styles.lastCell]}>Credit(₭)</Text>
                </View>
                <View style={styles.rowTable} wrap={false}>
                    <Text style={[styles.cell, styles.colCustId]}>{data?.customerID}</Text>
                    <Text style={[styles.cell, styles.colCustName]}>{data?.customers?.customerName}</Text>
                    <Text style={[styles.cell, styles.colInvoiceNo]}>{data?.invoiceNumber}</Text>
                    <Text style={[styles.cell, styles.colTotal]}>{formatCurrency(data?.totalPayment)}</Text>
                    <Text style={[styles.cell, styles.colCredit, styles.lastCell]}>{formatCurrency(data?.totalCredit)}</Text>
                </View>
            </View>
            <View style={styles.footerContainer} wrap={false}>
                <View style={styles.footerRow}>
                    <View style={styles.wordsBox}><Text>{toWords.convert(data?.totalCredit || 0)}</Text></View>
                    <View style={styles.sideLabel}><Text>Credit ₭</Text></View>
                    <View style={styles.sideValue}><Text>{formatCurrency(data?.totalCredit)}</Text></View>
                </View>
                <View style={styles.footerRow}>
                    <View style={styles.remarkLabel}><Text>Remark</Text></View>
                    <View style={styles.remarkContent}><Text>{data?.comment || data?.description || '-'}</Text></View>
                    <View style={styles.sideLabel}><Text>Status</Text></View>
                    <View style={styles.sideValue}><Text>{data?.status || 'unpaid'}</Text></View>
                </View>
                <View style={styles.footerRow}>
                    <View style={styles.remarkLabelEnd}><Text></Text></View>
                    <View style={styles.remarkContentEnd}><Text></Text></View>
                    <View style={styles.sideLabel}><Text>Approve Date</Text></View>
                    <View style={styles.sideValue}><Text>{data?.ApproveDate ? moment(data?.ApproveDate).format('DD-MM-yyyy') : '-'}</Text></View>
                </View>
                <View style={styles.footerRow}>
                    <View style={styles.grandTotalLabel}><Text>Credit Total</Text></View>
                    <View style={styles.grandTotalValue}><Text>{formatCurrency(data?.totalCredit)}</Text></View>
                </View>
            </View>
            <View style={{ marginTop: 25, flexDirection: 'row', justifyContent: 'space-between' }} wrap={false}>
                <View style={{ width: '48%', textAlign: 'left' }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Request By: {data?.CreateBy}</Text>
                    <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                    <Text style={{ fontSize: 9, marginTop: 5 }}>Date : {moment(data?.invoiceDate).format('DD-MM-yyyy')}</Text>
                </View>
                <View style={{ width: '48%', textAlign: 'left' }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Approve By: {data?.ApproveBy || '....................'}</Text>
                    <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                    <Text style={{ fontSize: 9, marginTop: 5 }}>Date : {data?.ApproveDate ? moment(data?.ApproveDate).format('DD-MM-yyyy') : '....................'}</Text>
                </View>
            </View>
        </Page>
        {data?.referenceImage && (
            <Page size="A4">
                <Image src={data.referenceImage} style={{ objectFit: 'contain', margin: 20 }} />
            </Page>
        )}
    </Document>
);

const CreditInvoicePDF = ({ data }) => {
    if (!data) return null;
    return (
        <>
            <PdfPreview document={<MyDocument data={data} />} fileName={data?.invoiceNumber} />
        </>
    );
};

export default CreditInvoicePDF;
